import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'sv' | 'en';

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (language: Language) => void;
  getText: (field: string | MultiLangText | undefined, fallback?: string) => string;
}

export interface MultiLangText {
  en?: string;
  sv?: string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('sv');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'sv' || savedLanguage === 'en')) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
    localStorage.setItem('language', language);
  };

  const getText = (field: string | MultiLangText | undefined, fallback: string = ''): string => {
    if (!field) return fallback;
    
    // If it's a string, return it directly (legacy format)
    if (typeof field === 'string') {
      return field;
    }
    
    // If it's a multi-language object, use fallback strategy
    if (typeof field === 'object' && field !== null) {
      // 1. Try current language
      if (field[currentLanguage]) {
        return field[currentLanguage];
      }
      
      // 2. Try Swedish as fallback
      if (field.sv) {
        return field.sv;
      }
      
      // 3. Try English as fallback
      if (field.en) {
        return field.en;
      }
    }
    
    return fallback;
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, getText }}>
      {children}
    </LanguageContext.Provider>
  );
}; 